import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Trophy } from "lucide-react";
import { API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Stars } from "@/components/CountryReviews";

const fetchStats = async () => {
  const r = await fetch(`${API}/reviews/stats`);
  if (!r.ok) throw new Error("stats failed");
  return r.json();
};

export default function TopRated({ countries, selectedCode, onSelect, limit = 5 }) {
  const { lang, t } = useLang();
  const { data: stats = {} } = useQuery({ queryKey: ["review-stats"], queryFn: fetchStats, staleTime: 60_000 });

  const ranked = Object.entries(stats)
    .map(([code, s]) => ({ code, ...s, country: countries.find((c) => c.code === code) }))
    .filter((r) => r.country)
    .sort((a, b) => b.avg - a.avg || b.count - a.count)
    .slice(0, limit);

  if (ranked.length === 0) return null;

  return (
    <div className="mt-6" data-testid="top-rated">
      <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-muted-foreground">
        <Trophy className="w-3 h-3 text-ochre" /> {t("top_rated")}
      </div>
      <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {ranked.map((r, i) => (
          <li key={r.code}>
            <button
              onClick={() => onSelect(r.code)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl border text-start transition-colors ${
                selectedCode === r.code ? "bg-ochre/10 border-ochre" : "border-stoneLine bg-card hover:bg-sandDeep/40"
              }`}
              data-testid={`top-rated-${r.code}`}
            >
              <span className={`w-6 h-6 shrink-0 rounded-full text-[11px] font-bold flex items-center justify-center ${i === 0 ? "bg-ochre text-white" : "bg-sandDeep text-ink"}`}>
                {i + 1}
              </span>
              <img src={r.country.flag_url} alt="" className="w-7 h-5 object-cover rounded-sm border border-stoneLine shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-ink truncate">{r.country.short_names[lang]}</span>
                <span className="flex items-center gap-1.5">
                  <Stars value={Math.round(r.avg)} size="w-3 h-3" />
                  <span className="text-xs text-muted-foreground" data-testid={`top-rated-avg-${r.code}`}>{r.avg} ({r.count})</span>
                </span>
              </span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
